import {
  ContentType,
  createReference,
  formatTime,
  getExtensionValue,
  isEmpty,
  MedplumClient,
} from "@medplum/core";
import {
  Address,
  ContactPoint,
  Coverage,
  DiagnosticReport,
  Observation,
  Patient,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { BillingInformation } from "@medplum/health-gorilla-core";
import { getAutocompleteSearchFunction } from "@medplum/health-gorilla-react";

import { Extensions } from "@/fhir/enums/extensions";
import { MedplumSystems } from "@/fhir/enums/medplum";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { calculateGestationalAgeInWeeksAndDays } from "@/pregnancy";
import { usDateFormat, usDateTimeFormat } from "@/utils/datetime";
import { getCodeableConceptCode, getCodeableConceptDisplay } from "@/utils/medplum";
import { trimPatientDemographics } from "@/utils/patient-demographics-trim";

import {
  HEALTH_GORILLA_OBSERVATION_INTERPRETATION_SYSTEMS,
  HealthGorillaLabOrderInternalStatus,
  HealthGorillaRequiredFields,
  LabOrderResultsSortKeys,
  TERMINAL_LAB_ORDER_INTERNAL_STATUSES,
  VALID_SEARCH_PARAMS_FILTERS,
} from "../types/labs";

export function getHealthGorillaAutocomplete(medplum: MedplumClient) {
  return getAutocompleteSearchFunction(medplum, {
    system: MedplumSystems.BotIdentifier,
    value: "health-gorilla-labs/autocomplete",
  });
}

export async function sendLabOrderToHealthGorilla(
  medplum: MedplumClient,
  labOrder: ServiceRequest,
  {
    refreshLabOrder = true,
    retryCount = 0,
    maxRetries = 3,
  }: { refreshLabOrder?: boolean; retryCount?: number; maxRetries?: number } = {},
) {
  try {
    await medplum.executeBot(
      {
        system: MedplumSystems.BotIdentifier,
        value: "health-gorilla-labs/send-to-health-gorilla",
      },
      labOrder,
      ContentType.FHIR_JSON,
    );

    if (refreshLabOrder) {
      return await medplum.readReference(createReference(labOrder), {
        cache: "no-cache",
      });
    }

    return labOrder;
  } catch (error) {
    if (retryCount < maxRetries) {
      const nextRetryCount = retryCount + 1;
      console.error(
        `Error sending lab order to Health Gorilla. Retrying (${nextRetryCount}/${maxRetries}).`,
        error,
      );
      return sendLabOrderToHealthGorilla(medplum, labOrder, {
        refreshLabOrder,
        retryCount: nextRetryCount,
        maxRetries,
      });
    }

    throw error;
  }
}

export function isFilteringLabOrders(searchParams: URLSearchParams): boolean {
  return Object.keys(Object.fromEntries(searchParams.entries())).some((key) =>
    VALID_SEARCH_PARAMS_FILTERS.includes(key),
  );
}

/**
 * Whether a lab orders list is showing its default view: every status, no column sort picked.
 *
 * That view is the one grouped by status priority. Once the user narrows to a single status group
 * or picks a column sort, the group order carries no information and a plain sorted query serves it.
 *
 * @param searchParams - The list's URL search params, holding its `status` filter and `sort` key.
 */
export function isDefaultLabOrderResultsView(searchParams: URLSearchParams): boolean {
  const status = searchParams.get("status") ?? HealthGorillaLabOrderInternalStatus.All;
  return !searchParams.get("sort") && status === HealthGorillaLabOrderInternalStatus.All;
}

/** One status group's contribution to the page on screen. */
export interface LabOrderStatusGroupSlice {
  /** Position of the group in the dashboard's status order. */
  groupIndex: number;
  /** Rows to skip within that group. */
  offset: number;
  /** Rows to take from that group. */
  count: number;
}

/**
 * Maps one page of the lab orders list onto the status groups it spans.
 *
 * The groups sit back to back in priority order, as one continuous list, so a page can start part
 * way through a group and run on into the next. Each slice returned is the offset and length to
 * request from its group, in the order the rows belong on screen.
 *
 * @param counts - Total lab orders per status group, in the dashboard's status order.
 * @param currentPage - 1-based page being displayed.
 * @param pageSize - Rows per page.
 * @returns One slice per group the page touches, groups it misses left out.
 */
export function planLabOrderStatusGroupPages(
  counts: readonly number[],
  currentPage: number,
  pageSize: number,
): LabOrderStatusGroupSlice[] {
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const slices: LabOrderStatusGroupSlice[] = [];
  let groupStart = 0;

  counts.forEach((count, groupIndex) => {
    const groupEnd = groupStart + count;
    const from = Math.max(pageStart, groupStart);
    const to = Math.min(pageEnd, groupEnd);

    if (to > from) {
      slices.push({ groupIndex, offset: from - groupStart, count: to - from });
    }

    groupStart = groupEnd;
  });

  return slices;
}

export function getLabOrderInternalStatusDisplay(labOrder: ServiceRequest | undefined): string {
  return (
    getCodeableConceptDisplay(
      labOrder?.category?.find(
        (category) =>
          category.coding?.[0]?.system ===
          QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
      ),
    ) ??
    labOrder?.status ??
    ""
  );
}

export function getLabOrderInternalStatus(labOrder: ServiceRequest | undefined): string {
  return (
    getCodeableConceptCode(
      labOrder?.category?.find(
        (category) =>
          category.coding?.[0]?.system ===
          QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
      ),
    ) ??
    labOrder?.status ??
    ""
  );
}

/**
 * Returns whether the lab order internal status is terminal (cancelled or denied).
 *
 * @param status - Internal status code from a ServiceRequest category or status enum.
 */
export function isTerminalLabOrderInternalStatus(
  status: string | HealthGorillaLabOrderInternalStatus | undefined,
): boolean {
  return TERMINAL_LAB_ORDER_INTERNAL_STATUSES.some((terminalStatus) => terminalStatus === status);
}

/**
 * Returns the received date for a lab order formatted as MM/dd/yyyy.
 * Uses `occurrenceDateTime` as the primary source; falls back to `meta.lastUpdated`.
 *
 * @param labOrder - The ServiceRequest representing the lab order.
 * @returns Formatted date string, or an empty string if neither field is set.
 */
export function getLabOrderReceivedDate(labOrder: ServiceRequest): string {
  return usDateFormat(labOrder.occurrenceDateTime || labOrder.meta?.lastUpdated);
}

/**
 * Resolves the flow sheet row label for a lab subsection observation.
 * Manual urine-culture results embed bacteria amount in code.text / coding.display.
 */
export function getLabFlowSheetSubsectionLabel(observation: Observation): string {
  return (
    observation.code?.text?.trim() ||
    getCodeableConceptDisplay(observation.code) ||
    getCodeableConceptCode(observation.code)
  );
}

/** Stable flow sheet row key; includes label so amount buckets stay distinct. */
export function getLabFlowSheetSubsectionKey(observation: Observation): string {
  const code = getCodeableConceptCode(observation.code);
  const label = getLabFlowSheetSubsectionLabel(observation);
  return `${code}::${label}`;
}

export function getLabObservationResultDisplay(observation: Observation): string {
  const VALUE_TYPES = [
    "valueQuantity",
    "valueCodeableConcept",
    "valueString",
    "valueBoolean",
    "valueInteger",
    "valueRange",
    "valueRatio",
    "valueTime",
    "valueDateTime",
    "valuePeriod",
  ] as const;

  const availableKey = VALUE_TYPES.find((key) => !isEmpty(observation[key]));
  if (!availableKey) return "";

  switch (availableKey) {
    case "valueQuantity":
      return observation[availableKey]?.value?.toString() ?? "";
    case "valueCodeableConcept":
      return getCodeableConceptDisplay(observation[availableKey]);
    case "valueBoolean":
      return observation[availableKey] ? "Yes" : "No";
    case "valueInteger":
      return observation[availableKey]?.toString() ?? "";
    case "valueRange":
      if (observation[availableKey]?.low?.value && observation[availableKey]?.high?.value) {
        return `${observation[availableKey]?.low?.value?.toString()} - ${observation[availableKey]?.high?.value?.toString()}`;
      } else if (observation[availableKey]?.low?.value) {
        return `${observation[availableKey]?.low?.value?.toString()} -`;
      } else if (observation[availableKey]?.high?.value) {
        return `- ${observation[availableKey]?.high?.value?.toString()}`;
      }
      return "";
    case "valueRatio":
      if (
        observation[availableKey]?.numerator?.value &&
        observation[availableKey]?.denominator?.value
      ) {
        return `${observation[availableKey]?.numerator?.value?.toString()}/${observation[availableKey]?.denominator?.value?.toString()}`;
      } else if (observation[availableKey]?.numerator?.value) {
        return `${observation[availableKey]?.numerator?.value?.toString()}/`;
      } else if (observation[availableKey]?.denominator?.value) {
        return `/${observation[availableKey]?.denominator?.value?.toString()}`;
      }
      return "";
    case "valueTime":
      return formatTime(observation[availableKey]);
    case "valueDateTime":
      return usDateTimeFormat(observation[availableKey]);
    case "valuePeriod":
      if (observation[availableKey]?.start && observation[availableKey]?.end) {
        return `${usDateTimeFormat(observation[availableKey]?.start)} - ${usDateTimeFormat(observation[availableKey]?.end)}`;
      } else if (observation[availableKey]?.start) {
        return `${usDateTimeFormat(observation[availableKey]?.start)} -`;
      } else if (observation[availableKey]?.end) {
        return `- ${usDateTimeFormat(observation[availableKey]?.end)}`;
      }
      return "";
    default:
      return observation[availableKey]?.toString() ?? "";
  }
}

export function validatePatientCoverages(
  coverages: Coverage[],
): BillingInformation["patientCoverage"] {
  if (coverages.length === 0) {
    return coverages as [];
  } else if (coverages.length === 1) {
    return coverages as [Coverage];
  } else if (coverages.length === 2) {
    return coverages as [Coverage, Coverage];
  } else if (coverages.length === 3) {
    return coverages as [Coverage, Coverage, Coverage];
  }

  throw new Error("Invalid number of coverages");
}

export function getValidLabOrderResultsSortKey(sortKey: LabOrderResultsSortKeys | undefined) {
  if (!sortKey) return undefined;

  const parsedSortKey = sortKey.startsWith("-") ? sortKey.slice(1) : sortKey;
  if (Object.values(LabOrderResultsSortKeys).includes(parsedSortKey as LabOrderResultsSortKeys)) {
    return sortKey;
  }

  return undefined;
}

export function getLabObservationResultInterpretation(
  result: Observation,
  isPatient: boolean,
): [string, string] {
  const interpretation = result.interpretation?.[0].coding?.find((coding) =>
    HEALTH_GORILLA_OBSERVATION_INTERPRETATION_SYSTEMS.some((system) => system === coding.system),
  );

  const interpretationColor = !interpretation || interpretation.code === "N" ? "green.7" : "red.7";

  let interpretationDisplay = interpretation?.display ?? "";
  if (interpretation && interpretation.code !== "N" && isPatient) {
    interpretationDisplay = "Not Normal";
  }

  return [interpretationDisplay, interpretationColor];
}

export function formatGestationalWeeksForLabFlowSheet(
  edd: string | undefined,
  collectionDate: string,
): string {
  if (!edd) return "";
  const { weeks, days } = calculateGestationalAgeInWeeksAndDays(edd, collectionDate);
  if (weeks < 0) return "";
  return `${weeks}.${days}`;
}

export function getLabOrderResultRangeDisplay(observation: Observation): string {
  return (
    observation.referenceRange
      ?.map((range) => range.text)
      .filter((text): text is string => !!text?.trim())
      .join(", ") ?? ""
  );
}

export function hasValidAddress(address: Address[] | undefined): boolean {
  return (
    address?.some(
      (addr) =>
        (addr.line?.[0]?.trim()?.length ?? 0) > 0 &&
        (addr.city?.trim()?.length ?? 0) > 0 &&
        (addr.state?.trim()?.length ?? 0) > 0 &&
        (addr.postalCode?.trim()?.length ?? 0) > 0,
    ) ?? false
  );
}

const LAB_ORDER_PHONE_USES = new Set<NonNullable<ContactPoint["use"]>>(["home", "mobile"]);

/**
 * Returns whether the patient has a non-empty home or mobile phone on their profile.
 * Work and other phone types do not satisfy lab-ordering requirements.
 *
 * @param telecom - Patient telecom entries (typically after demographic trimming).
 * @returns `true` when a home or mobile phone number is present.
 */
export function hasHomeOrMobilePhoneNumber(telecom: Patient["telecom"] | undefined): boolean {
  return (
    telecom?.some((entry) => {
      if (entry.system !== "phone") return false;
      if (entry.use === undefined || !LAB_ORDER_PHONE_USES.has(entry.use)) return false;
      const value = entry.value?.trim();
      return Boolean(value);
    }) ?? false
  );
}

export function hasRequiredFieldsForHealthGorilla(
  patient: Patient | undefined,
): HealthGorillaRequiredFields {
  /* Required fields to use Health Gorilla:
    - Client Full Name (First and Last)
    - Language
    - Sex Assigned at Birth
    - Legal Sex
    - Date of Birth (DOB)
    - Home or Mobile Phone Number (work and other phone types do not count)
    - Full Address (Street Address, City, State, ZIP Code)
  */

  if (!patient)
    return {
      hasFullName: false,
      hasBirthDate: false,
      hasLegalSex: false,
      hasSexAssignedAtBirth: false,
      hasLanguages: false,
      hasPhoneNumber: false,
      hasAddress: false,
    };

  const trimmedPatient = trimPatientDemographics(patient);

  const hasFullName =
    (trimmedPatient.name?.[0]?.given?.length ?? 0) > 0 &&
    (trimmedPatient.name?.[0]?.family?.length ?? 0) > 0;
  const hasBirthDate = trimmedPatient.birthDate !== undefined;
  const hasLegalSex = trimmedPatient.gender !== undefined;
  const hasSexAssignedAtBirth =
    getExtensionValue(patient, Extensions.SexAssignedAtBirth) !== undefined;
  const hasLanguages = (trimmedPatient.communication?.length ?? 0) > 0;
  const hasPhoneNumber = hasHomeOrMobilePhoneNumber(trimmedPatient.telecom);
  const hasAddress = hasValidAddress(trimmedPatient.address);

  return {
    hasFullName,
    hasBirthDate,
    hasLegalSex,
    hasSexAssignedAtBirth,
    hasLanguages,
    hasPhoneNumber,
    hasAddress,
  };
}

/**
 * Whether a lab order is an uploaded imaging result. `uploadManualResult` marks those with the
 * imaging category on top of the laboratory one.
 */
export function isUploadedImagingResult(labOrder: ServiceRequest | undefined): boolean {
  return !!labOrder?.category?.some((category) =>
    category.coding?.some(
      (coding) => coding.system === Systems.SNOMED && coding.code === SNOMEDCodes.ImagingProcedure,
    ),
  );
}

/**
 * The indication and reading of an uploaded imaging result: `ServiceRequest.reasonCode.text` and
 * `DiagnosticReport.conclusion`. Both are empty for every other lab order.
 */
export function getUploadedImagingDetails(
  labOrder: ServiceRequest | undefined,
  diagnosticReport: DiagnosticReport | undefined,
): { indication: string; conclusion: string } {
  if (!isUploadedImagingResult(labOrder)) return { indication: "", conclusion: "" };
  return {
    indication: labOrder?.reasonCode?.[0]?.text ?? "",
    conclusion: diagnosticReport?.conclusion ?? "",
  };
}

/** The optional title an uploaded imaging result was given. Empty for every other lab order. */
export function getUploadedImagingTitle(labOrder: ServiceRequest | undefined): string {
  if (!isUploadedImagingResult(labOrder)) return "";
  return (
    labOrder?.extension?.find((extension) => extension.url === Extensions.UploadedResultTitle)
      ?.valueString ?? ""
  );
}
